import { useEventListener } from 'ahooks'
// @ts-ignore
import * as d3flextree from 'd3-flextree'
import React from 'react'

import { Department } from '@/modules/department'
import { PpObjective } from '@/schemas'

export interface Node {
  children: Node[]
  data: Department & {
    objectives: PpObjective[]
  }
  depth: number
  height: number
  id: string
  parent: number
  width: number
  x: number
  y: number
  size: [number, number]
  xSize: number
  ySize: number
  extents: { top: number; bottom: number; left: number; right: number }
  ancestors: () => Node[]
  path: (a: Node) => Node[]
}

export const defaultSize = {
  link: {
    height: 80,
  },
  node: {
    width: 370,
    lineHeight: 16.25,
    emptyHeight: 30,
  },
  margin: {
    top: 50,
    bottom: 100,
    left: 0,
    right: 0,
  },
}

export const getTree = (departments: Department) => {
  const layout = d3flextree
    .flextree()
    .nodeSize((node: Node) => {
      const length = node.data.objectives.length
      return [
        defaultSize.node.width,
        defaultSize.node.emptyHeight + length * defaultSize.node.lineHeight + defaultSize.link.height,
      ]
    })
    .spacing((nodeA: Node, nodeB: Node) => nodeA.path(nodeB).length + 20)
  const hierarchy = layout.hierarchy(departments)
  return layout(hierarchy)
}

export const drawPath = (source: Node, filterFn: any = () => true) => {
  const children = source.children.filter(filterFn)
  if (children.length === 0) {
    return null
  }
  const [x0, y0] = [source.x + source.xSize / 2, source.y + source.ySize - defaultSize.link.height]
  let [xMin, xMax] = [x0, x0]
  const yMid = y0 + (children[0].y - y0) / 2
  let context = ''
  context += `M${x0},${y0}`
  context += `L${x0},${yMid}`
  children.forEach((target: Node) => {
    const [x1, y1] = [target.x + source.xSize / 2, target.y]
    context += `M${x1},${yMid}`
    context += `L${x1},${y1}`
    xMin = Math.min(xMin, x1)
    xMax = Math.max(xMax, x1)
  })
  context += `M${xMin},${yMid}`
  context += `L${xMax},${yMid}`
  return context
}

export const useMoveDom = (ref: React.MutableRefObject<HTMLDivElement | null>) => {
  const mouseRef = React.useRef({
    curDown: false,
    curDownX: 0,
    curDownY: 0,
    curDownScrollLeft: 0,
    curDownScrollTop: 0,
  })

  useEventListener(
    'mousedown',
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!ref.current) {
        return
      }
      const m = mouseRef.current
      m.curDownX = e.clientX
      m.curDownY = e.clientY
      m.curDownScrollLeft = ref.current.scrollLeft
      m.curDownScrollTop = ref.current.scrollTop
      m.curDown = true
    },
    { target: ref }
  )

  useEventListener(
    'mouseup',
    () => {
      mouseRef.current.curDown = false
    },
    { target: ref }
  )

  useEventListener(
    'mouseout',
    () => {
      mouseRef.current.curDown = false
    },
    { target: ref }
  )

  useEventListener(
    'mousemove',
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!ref.current) {
        return
      }
      const m = mouseRef.current
      if (m.curDown) {
        ref.current.scrollLeft = m.curDownScrollLeft - (e.clientX - m.curDownX)
        ref.current.scrollTop = m.curDownScrollTop - (e.clientY - m.curDownY)
      }
    },
    { target: ref }
  )
}
